import { createContext, useContext } from "react";

/**
 * Registers the floating action button the mobile drawer should carry with it as it is
 * pulled down.
 *
 * A drawer FAB is viewport-fixed, so without this it keeps floating over the sheet as the
 * sheet shrinks — ending up on top of the collapsed peek, obscuring the header and close
 * button that the peek exists to keep visible. Registering hands the FAB's vertical
 * transform to the drawer: once a pull-down crosses into the range where release would
 * collapse, the FAB rides down with the sheet's top edge and is fully off-screen by the
 * time the sheet rests at the peek. Expanding back to any open snap returns it.
 *
 * Pass the callback as a `ref` on the FAB's positioned wrapper (the element whose
 * `transform` may be animated). `SlottedWindow`'s drawer FAB registers itself; register
 * explicitly only for a FAB rendered outside it.
 *
 * Outside a drawer (desktop, docked, side panel) the callback is a stable no-op, so FAB
 * components can attach it unconditionally.
 */
export type RegisterDrawerFabAnchor = (node: HTMLElement | null) => void;

// Module-level so the default is referentially stable: an inline `() => {}` would be a
// new ref callback on every render, making React detach and reattach the node each time.
const noRegistration: RegisterDrawerFabAnchor = () => {};

export const DrawerFabAnchorContext = createContext<RegisterDrawerFabAnchor>(noRegistration);

export function useDrawerFabAnchor(): RegisterDrawerFabAnchor {
    return useContext(DrawerFabAnchorContext);
}
